import type { ActionBehaviours, ActionHandler } from '../tools/do-action.tool';

/** What a screen says after running its own button's code. A failed write may also throw; do_action reports that as failed. */
export type ScreenActionAnswer = { done: string; result?: unknown } | { refused: string };
export type ScreenAction = (values: Record<string, unknown>) => Promise<ScreenActionAnswer>;

const offered = new Map<string, ScreenAction>();

/**
 * A mounted screen offers the actions it can run, and withdraws them when it goes. So a behaviour never needs to know
 * which component holds an action, or whether a tab inside a screen is the one drawn; an action nobody offers isn't on
 * the builder's screen. Free of Angular so the desktop app's scripted checks load this very file.
 */
export function offerScreenActions(actions: Record<string, ScreenAction>): () => void {
  for (const [id, action] of Object.entries(actions)) offered.set(id, action);
  return () => {
    for (const [id, action] of Object.entries(actions)) {
      // The next screen is built before the last one is destroyed, and may have offered the same id already.
      if (offered.get(id) === action) offered.delete(id);
    }
  };
}

export const runOnScreen: ActionHandler = async (values, _ctx, row) => {
  const action = offered.get(row.id);
  if (!action) {
    // Was (2026-10-30), until Bastion's list was seen to have no reload: "Every screen with a list offers a reload, so one that doesn't simply has no list", answering "The builder's screen has no list to reload."
    // A row for every screen has nowhere to send Claude, and a screen without a reload may still show a list.
    if (row.screen === 'everywhere') return { outcome: 'refused', refusal: `The builder's screen doesn't offer a reload. Nothing was changed.` };
    const where = row.panel ? `the ${row.panel} panel of ${row.screen}` : row.screen;
    return { outcome: 'refused', refusal: `${row.label} is on ${where}, which isn't on the builder's screen. Nothing was changed; call navigate first.` };
  }
  const answer = await action(values);
  if ('refused' in answer) return { outcome: 'refused', refusal: answer.refused };
  return { outcome: 'done', changed: [answer.done], ...(answer.result === undefined ? {} : { result: answer.result }) };
};

const ON_SCREEN = [
  'create-page', 'create-role', 'create-workspace', 'rename-role', 'set-chart-style',
  'add-page-entity', 'add-page-variable', 'save-page-data',
  'pick-tenant', 'set-mobile-app-name', 'set-app-bars', 'save-app-review-details', 'save-app-store-listing', 'save-apple-rejection-notes',
  'save-privacy-policy-draft', 'set-developer-mode', 'tick-website-checklist', 'build-dev-apk', 'refresh-apple-status',
  'search-projects', 'search-pages', 'filter-pages', 'reload-list', 'toggle-graph-engine',
  'dismiss-failed-task',
];

export const FAST_LANE_BEHAVIOURS: ActionBehaviours = Object.fromEntries(ON_SCREEN.map((id) => [id, { fast: runOnScreen }]));
